import { seesMoney } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { counterpartiesQuery, projectQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Input, Select } from '../../ui/form';

/**
 * Дополнительный отбор единого реестра (УЧ-01): номер или текст, объект, марка (раздел ПСД), подрядчик,
 * ответственный и срок до даты. Значения живут в адресе страницы — ссылкой можно поделиться.
 */

export type ExtraFilter = {
  q?: string;
  object?: string;
  mark?: string;
  contractor?: string;
  assignee?: string;
  due?: string;
};

export const extraQuery = (s: ExtraFilter, remarks: boolean) => ({
  q: s.q,
  objectId: s.object,
  mark: s.mark,
  ...(remarks ? { contractorId: s.contractor, assigneeId: s.assignee, dueTo: s.due } : {}),
});

export function ExtraFilters({
  value,
  projectId,
  remarks,
  onChange,
}: {
  value: ExtraFilter;
  projectId?: string;
  remarks: boolean;
  onChange: (patch: ExtraFilter) => void;
}) {
  const active = (
    remarks
      ? [value.object, value.mark, value.contractor, value.assignee, value.due]
      : [value.object, value.mark]
  ).filter(Boolean).length;
  // Текст ищется только тем, кто видит суммы (иначе по ответам можно угадать скрытую сумму) — остальным по номеру
  const byText = seesMoney(useMe().access);
  const [open, setOpen] = useState(active > 0);
  const [q, setQ] = useState(value.q ?? '');
  const [mark, setMark] = useState(value.mark ?? '');
  // Из адреса — только чужое значение (назад, ссылка), а не эхо своего ввода: иначе пропадают пробелы и буквы
  useEffect(() => setQ((cur) => (cur.trim() === (value.q ?? '') ? cur : (value.q ?? ''))), [value.q]);
  useEffect(
    () => setMark((cur) => (cur.trim() === (value.mark ?? '') ? cur : (value.mark ?? ''))),
    [value.mark],
  );
  // Текст уходит в отбор, когда человек перестал печатать
  useEffect(() => {
    const t = setTimeout(() => {
      if ((value.q ?? '') !== q.trim()) onChange({ q: q.trim() || undefined });
      if ((value.mark ?? '') !== mark.trim()) onChange({ mark: mark.trim() || undefined });
    }, 400);
    return () => clearTimeout(t);
  }, [q, mark, value.q, value.mark, onChange]);
  const project = useQuery({
    ...projectQuery(projectId ?? ''),
    enabled: Boolean(projectId) && open,
    retry: false,
  });
  const contractors = useQuery({
    ...counterpartiesQuery('contractor'),
    enabled: remarks && open,
    retry: false,
  });
  const people = useQuery({
    queryKey: ['supervision-people', projectId, 'respond'],
    queryFn: () =>
      api<Array<{ id: string; fullName: string }>>('/supervision/people', {
        query: { projectId, perm: 'respond' },
      }),
    enabled: remarks && open && Boolean(projectId),
    retry: false,
  });
  return (
    <>
      <Input
        type="search"
        aria-label="Номер или текст"
        className="h-11 md:h-10 md:w-56"
        placeholder={
          byText
            ? remarks
              ? 'Номер или текст замечания'
              : 'Номер или тема вопроса'
            : remarks
              ? 'Номер замечания'
              : 'Номер вопроса'
        }
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <Button
        variant="ghost"
        className="h-11 md:h-10"
        icon={<SlidersHorizontal className="size-4" />}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        Ещё отбор{active ? ` (${active})` : ''}
      </Button>
      {open ? (
        <div className="flex w-full flex-col gap-2 md:flex-row md:flex-wrap">
          {projectId ? (
            <Select
              aria-label="Объект"
              className="h-11 md:h-10 md:w-56"
              value={value.object ?? ''}
              onChange={(e) => onChange({ object: e.target.value || undefined })}
            >
              <option value="">Все объекты</option>
              {(project.data?.objects ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} {o.name}
                </option>
              ))}
            </Select>
          ) : null}
          <Input
            aria-label="Марка комплекта"
            className="h-11 md:h-10 md:w-40"
            placeholder="Марка: КЖ, ОВ…"
            value={mark}
            onChange={(e) => setMark(e.target.value)}
          />
          {remarks ? (
            <>
              <Select
                aria-label="Подрядчик"
                className="h-11 md:h-10 md:w-56"
                value={value.contractor ?? ''}
                onChange={(e) => onChange({ contractor: e.target.value || undefined })}
              >
                <option value="">Все подрядчики</option>
                {(contractors.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.shortName ?? c.name}
                  </option>
                ))}
              </Select>
              {projectId ? (
                <Select
                  aria-label="Ответственный"
                  className="h-11 md:h-10 md:w-56"
                  value={value.assignee ?? ''}
                  onChange={(e) => onChange({ assignee: e.target.value || undefined })}
                >
                  <option value="">Все ответственные</option>
                  {(people.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.fullName}
                    </option>
                  ))}
                </Select>
              ) : null}
              <span className="flex items-center gap-2 text-[13px] text-ink-2">
                Срок до
                <Input
                  type="date"
                  aria-label="Срок до"
                  className="h-11 md:h-10"
                  value={value.due ?? ''}
                  onChange={(e) => onChange({ due: e.target.value || undefined })}
                />
              </span>
            </>
          ) : null}
          {active ? (
            <Button
              variant="ghost"
              className="h-11 md:h-10"
              onClick={() =>
                onChange({
                  object: undefined,
                  mark: undefined,
                  contractor: undefined,
                  assignee: undefined,
                  due: undefined,
                })
              }
            >
              Сбросить отбор
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
